import React from "react";
import * as THREE from "three";
import { observer } from "mobx-react-lite";
import { FloorplannerStoreContext } from "../../../../store/floorplannerStore";
import { WallType } from "../../../../types/floorplannerTypes";
import type { CatalogSurfaceReference } from "../../products/product3DTypes";
import CatalogStandardMaterial from "../../materials/CatalogStandardMaterial";
import LitPatternMaterial from "../../materials/LitPatternMaterial";
import { WALL_FLOOR_CATALOG_TEXTURE_SIZE } from "../../materials/textureCache";
import {
  getBuiltInFurnitureMaterial,
  isGlassBuiltInFurnitureMaterial,
} from "../../materials/builtInFurnitureMaterials";
import { resolveMaterial } from "../../materials/materialResolver";
import { getWallColor } from "./wallGeometry";
import { resolveFillOpacity } from "../../../../utils/fillOpacity";
import { wallHasPerSideFaceMaterials } from "../../../../utils/wallFaceSides";

type Wall3DMaterialProps = {
  wall: WallType;
  color?: THREE.Color;
  /** Override wall.threeD.surfaceRef (e.g. per face-segment material). */
  surfaceRef?: CatalogSurfaceReference;
  /**
   * Render side. Defaults to DoubleSide so solid extruded walls stay filled
   * from every camera angle. Pass FrontSide only for split left/right faces.
   */
  side?: THREE.Side;
  /** Override wall fill opacity when the caller needs a non-wall default. */
  opacity?: number;
};

/**
 * Resolve which surfaceRef to render for this wall/face call.
 * In per-side mode the caller-supplied face ref is authoritative (including
 * "none"); otherwise fall back to the whole-wall surfaceRef.
 */
function resolveWallRenderSurfaceRef(
  wall: WallType,
  surfaceRef?: CatalogSurfaceReference,
): CatalogSurfaceReference | undefined {
  if (wallHasPerSideFaceMaterials(wall)) {
    return surfaceRef;
  }
  return surfaceRef ?? wall.threeD?.surfaceRef;
}

export function hasAssignedWallMaterial(
  wall: WallType,
  surfaceRef?: CatalogSurfaceReference,
): boolean {
  const resolved = resolveWallRenderSurfaceRef(wall, surfaceRef);
  if (wallHasPerSideFaceMaterials(wall)) {
    // Face owns its finish — ignore leftover wall-level materialId.
    return Boolean(resolved?.id);
  }
  return Boolean(resolved?.id || wall.threeD?.materialId);
}

export function shouldRenderWallPattern(
  wall: WallType,
  surfaceRef?: CatalogSurfaceReference,
): boolean {
  return Boolean(wall.fillPattern) && !hasAssignedWallMaterial(wall, surfaceRef);
}

/**
 * Resolve the Ready / built-in material currently assigned to a wall (or face).
 * Prefer the surface being rendered so a face override beats a stale wall materialId.
 */
export function getAssignedWallBuiltInMaterial(
  wall: WallType,
  surfaceRef?: CatalogSurfaceReference,
) {
  const resolved = resolveWallRenderSurfaceRef(wall, surfaceRef);
  if (wallHasPerSideFaceMaterials(wall)) {
    return getBuiltInFurnitureMaterial(resolved?.id);
  }
  return getBuiltInFurnitureMaterial(
    resolved?.id ?? wall.threeD?.materialId,
  );
}

const Wall3DMaterial: React.FC<Wall3DMaterialProps> = observer(
  ({ wall, color, surfaceRef, side = THREE.DoubleSide, opacity }) => {
    const floorplannerStore = React.useContext(FloorplannerStoreContext);
    const resolvedSurfaceRef = resolveWallRenderSurfaceRef(wall, surfaceRef);
    const renderPattern = shouldRenderWallPattern(wall, surfaceRef);
    const fillOpacity = opacity ?? resolveFillOpacity(wall);
    const fillIsTransparent = fillOpacity < 1;
    const assignedBuiltIn = getAssignedWallBuiltInMaterial(
      wall,
      surfaceRef,
    );
    const assignedIsGlass = isGlassBuiltInFurnitureMaterial(assignedBuiltIn);
    // Prefer explicit face color, then catalog surface tint, then wall default.
    const resolvedColor =
      color ??
      (resolvedSurfaceRef?.color !== undefined
        ? new THREE.Color(resolvedSurfaceRef.color)
        : undefined) ??
      resolveMaterial("wall", wall, {
        defaultColor: getWallColor(wall),
      }).color;
    const presetDefaults = assignedBuiltIn
      ? {
          roughness: assignedBuiltIn.roughness,
          metalness: assignedBuiltIn.metalness,
          // Admin / category tiles-per-metre when surfaceRef has no override.
          ...(assignedBuiltIn.repeatScale !== undefined
            ? { repeatScale: assignedBuiltIn.repeatScale }
            : {}),
        }
      : floorplannerStore.threeDMaterialDefaults.wall;

    if (renderPattern && wall.fillPattern) {
      return (
        <LitPatternMaterial
          fillColor={resolvedColor}
          fillPattern={wall.fillPattern}
          uvChannel={1}
          roughness={
            floorplannerStore.threeDMaterialDefaults.wall?.roughness ?? 0.78
          }
          metalness={
            floorplannerStore.threeDMaterialDefaults.wall?.metalness ?? 0
          }
          side={side}
          opacity={fillOpacity}
          transparent={fillIsTransparent}
          depthWrite={!fillIsTransparent}
        />
      );
    }

    // Glass Ready presets need MeshPhysicalMaterial transmission (same as shapes).
    if (
      hasAssignedWallMaterial(wall, resolvedSurfaceRef) &&
      assignedIsGlass &&
      assignedBuiltIn
    ) {
      return (
        <meshPhysicalMaterial
          color={resolvedColor}
          roughness={
            resolvedSurfaceRef?.roughness ?? assignedBuiltIn.roughness
          }
          metalness={0}
          transmission={assignedBuiltIn.transmission ?? 1}
          thickness={assignedBuiltIn.thickness ?? 0.05}
          ior={assignedBuiltIn.ior ?? 1.5}
          transparent
          opacity={fillOpacity < 1 ? fillOpacity : 1}
          side={side}
          depthWrite={false}
        />
      );
    }

    return (
      <CatalogStandardMaterial
        // Remount when Ready material is cleared so a prior albedo map cannot stick.
        key={
          resolvedSurfaceRef?.id ??
          (wallHasPerSideFaceMaterials(wall)
            ? "wall-face-default"
            : wall.threeD?.materialId ?? "wall-default")
        }
        color={resolvedColor}
        surfaceRef={resolvedSurfaceRef}
        defaults={presetDefaults}
        // Metre-space uv1 so wallpaper tiles every X metres instead of stretching.
        uvChannel={1}
        maxTextureSize={WALL_FLOOR_CATALOG_TEXTURE_SIZE}
        forceTextureWidth
        side={side}
        opacity={fillOpacity}
        transparent={fillIsTransparent}
        depthWrite={!fillIsTransparent}
      />
    );
  },
);

export default Wall3DMaterial;
